<div class="print-instruction-manager">
  <!-- Header Section -->
  <div class="header-section">
    <div class="row align-items-center">
      <div class="col-md-8">
        <h2 class="page-title">
          <i class="fa fa-print me-2"></i>
          Print Instruction Manager
        </h2>
        <p class="page-subtitle">Manage policy print configurations and settings</p>
      </div>
      <div class="col-md-4 text-end">
        <button 
          type="button" 
          class="btn btn-primary btn-create" 
          (click)="openCreateModal()"
          [disabled]="isLoading">
          <i class="fa fa-plus me-2"></i>
          Add New Instruction
        </button>
      </div>
    </div>
  </div>

  <!-- Search and Filter Section -->
  <div class="filter-section">
    <form [formGroup]="searchForm" class="filter-form">
      <div class="row g-3">
        <div class="col-md-4">
          <div class="form-group">
            <label class="form-label">
              <i class="fa fa-search me-1"></i>
              Search Policy Type
            </label>
            <input 
              type="text" 
              class="form-control" 
              formControlName="searchTerm"
              placeholder="Search by policy type...">
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group">
            <label class="form-label">
              <i class="fa fa-building me-1"></i>
              Policy Company
            </label>
            <select class="form-select" formControlName="company">
              <option value="all">All Companies</option>
              <option *ngFor="let company of policyCompanies" [value]="company">
                {{ company }}
              </option>
            </select>
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group">
            <label class="form-label">
              <i class="fa fa-tags me-1"></i>
              Aggregate Type
            </label>
            <select class="form-select" formControlName="aggregateType">
              <option value="all">All Types</option>
              <option *ngFor="let type of aggregateTypes" [value]="type">
                {{ type }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </form>
  </div>

  <!-- Results Section -->
  <div class="results-section">
    <div class="results-header">
      <h5 class="mb-0">
        <i class="fa fa-list me-2"></i>
        Print Instructions
      </h5>
      <span class="badge bg-info">
        {{ filteredInstructions.length }} of {{ printInstructions.length }} instructions
      </span>
    </div>

    <!-- Loading State -->
    <div *ngIf="isLoading" class="loading-container">
      <div class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-3">Loading print instructions...</p>
      </div>
    </div>

    <!-- Instructions Table -->
    <div *ngIf="!isLoading" class="table-container">
      <div class="table-responsive">
        <table class="table table-hover instructions-table">
          <thead class="table-dark">
            <tr>
              <th>Policy Type</th>
              <th>Company</th>
              <th>Aggregate Type</th>
              <th>Print Pages</th>
              <th>Age Range</th>
              <th class="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let instruction of filteredInstructions; trackBy: trackByPolicyType">
              <td>
                <div class="policy-type-cell">
                  <strong>{{ instruction.policyType }}</strong>
                </div>
              </td>
              <td>
                <div class="company-cell">
                  <img 
                    [src]="instruction.policyCompanyImage" 
                    [alt]="instruction.policyCompany"
                    class="company-logo">
                  <span class="company-name">{{ instruction.policyCompany }}</span>
                </div>
              </td>
              <td>
                <span class="badge badge-aggregate">{{ instruction.aggregatePolicyType }}</span>
              </td>
              <td>
                <div class="print-pages-cell">
                  <small>{{ getPrintPagesSummary(instruction) }}</small>
                </div>
              </td>
              <td>
                <div class="age-range-cell" *ngIf="instruction.minAgeAllowed || instruction.maxAgeAllowed">
                  <small>
                    {{ instruction.minAgeAllowed || 'N/A' }} - {{ instruction.maxAgeAllowed || 'N/A' }} years
                  </small>
                </div>
                <small *ngIf="!instruction.minAgeAllowed && !instruction.maxAgeAllowed" class="text-muted">
                  No age limit
                </small>
              </td>
              <td class="text-center">
                <div class="action-buttons">
                  <button 
                    type="button" 
                    class="btn btn-sm btn-outline-primary me-2" 
                    (click)="openEditModal(instruction)"
                    [disabled]="isLoading">
                    <i class="fa fa-edit"></i>
                  </button>
                  <button 
                    type="button" 
                    class="btn btn-sm btn-outline-danger" 
                    (click)="deleteInstruction(instruction)"
                    [disabled]="isLoading">
                    <i class="fa fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- No Results -->
      <div *ngIf="filteredInstructions.length === 0" class="no-results">
        <div class="text-center py-4">
          <i class="fa fa-info-circle fa-3x text-muted mb-3"></i>
          <h5>No print instructions found</h5>
          <p class="text-muted">Try adjusting your search filters or add a new instruction.</p>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Edit/Create Modal -->
<div class="modal" [class.show]="showModal" [style.display]="showModal ? 'block' : 'none'">
  <div class="modal-dialog modal-lg">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="fa fa-{{ isEditMode ? 'edit' : 'plus' }} me-2"></i>
          {{ isEditMode ? 'Edit' : 'Create' }} Print Instruction
        </h5>
        <button type="button" class="btn-close" (click)="closeModal()"></button>
      </div>
      
      <form [formGroup]="editForm" (ngSubmit)="onSubmit()">
        <div class="modal-body">
          <div class="row g-3">
            <!-- Policy Type -->
            <div class="col-md-6">
              <div class="form-group">
                <label class="form-label required">Policy Type</label>
                <input 
                  type="text" 
                  class="form-control"
                  [class.is-invalid]="isFieldInvalid('policyType')"
                  formControlName="policyType"
                  [readonly]="isEditMode"
                  placeholder="Enter policy type">
                <div class="invalid-feedback">{{ getFieldError('policyType') }}</div>
              </div>
            </div>

            <!-- Policy Company -->
            <div class="col-md-6">
              <div class="form-group">
                <label class="form-label required">Policy Company</label>
                <select 
                  class="form-select"
                  [class.is-invalid]="isFieldInvalid('policyCompany')"
                  formControlName="policyCompany">
                  <option value="">Select company</option>
                  <option *ngFor="let company of policyCompanies" [value]="company">
                    {{ company }}
                  </option>
                </select>
                <div class="invalid-feedback">{{ getFieldError('policyCompany') }}</div>
              </div>
            </div>

            <!-- Aggregate Policy Type -->
            <div class="col-md-6">
              <div class="form-group">
                <label class="form-label required">Aggregate Policy Type</label>
                <select 
                  class="form-select"
                  [class.is-invalid]="isFieldInvalid('aggregatePolicyType')"
                  formControlName="aggregatePolicyType">
                  <option value="">Select aggregate type</option>
                  <option *ngFor="let type of aggregateTypes" [value]="type">
                    {{ type }}
                  </option>
                </select>
                <div class="invalid-feedback">{{ getFieldError('aggregatePolicyType') }}</div>
              </div>
            </div>

            <!-- Policy Company Image -->
            <div class="col-md-6">
              <div class="form-group">
                <label class="form-label required">Company Image Path</label>
                <input 
                  type="text" 
                  class="form-control"
                  [class.is-invalid]="isFieldInvalid('policyCompanyImage')"
                  formControlName="policyCompanyImage"
                  placeholder="assets/company-logo.png">
                <div class="invalid-feedback">{{ getFieldError('policyCompanyImage') }}</div>
              </div>
            </div>

            <!-- Age Range -->
            <div class="col-md-6">
              <div class="form-group">
                <label class="form-label">Minimum Age Allowed</label>
                <input 
                  type="number" 
                  class="form-control"
                  [class.is-invalid]="isFieldInvalid('minAgeAllowed')"
                  formControlName="minAgeAllowed"
                  min="1" max="100"
                  placeholder="18">
                <div class="invalid-feedback">{{ getFieldError('minAgeAllowed') }}</div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="form-group">
                <label class="form-label">Maximum Age Allowed</label>
                <input 
                  type="number" 
                  class="form-control"
                  [class.is-invalid]="isFieldInvalid('maxAgeAllowed')"
                  formControlName="maxAgeAllowed"
                  min="1" max="100"
                  placeholder="70">
                <div class="invalid-feedback">{{ getFieldError('maxAgeAllowed') }}</div>
              </div>
            </div>
          </div>

          <!-- Print Options -->
          <div class="print-options-section">
            <h6 class="section-title">
              <i class="fa fa-print me-2"></i>
              Print Page Options
            </h6>
            <div class="row g-3">
              <div class="col-md-4">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" formControlName="printPage" id="printPage">
                  <label class="form-check-label" for="printPage">Print Page 1</label>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" formControlName="printPage2" id="printPage2">
                  <label class="form-check-label" for="printPage2">Print Page 2</label>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" formControlName="printPage3" id="printPage3">
                  <label class="form-check-label" for="printPage3">Print Page 3</label>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" formControlName="printPageFinance" id="printPageFinance">
                  <label class="form-check-label" for="printPageFinance">Print Finance Page</label>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" formControlName="printPageCarCPA" id="printPageCarCPA">
                  <label class="form-check-label" for="printPageCarCPA">Print Car CPA Page</label>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" formControlName="printOnlyFirstPage" id="printOnlyFirstPage">
                  <label class="form-check-label" for="printOnlyFirstPage">Print Only First Page</label>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" formControlName="printPageGeneralCPA" id="printPageGeneralCPA">
                  <label class="form-check-label" for="printPageGeneralCPA">Print General CPA Page</label>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" formControlName="printPageIfcoTokio" id="printPageIfcoTokio">
                  <label class="form-check-label" for="printPageIfcoTokio">Print Iffco Tokio Page</label>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="closeModal()">Cancel</button>
          <button type="submit" class="btn btn-primary" [disabled]="editForm.invalid || isLoading">
            <span *ngIf="isLoading" class="spinner-border spinner-border-sm me-2"></span>
            <i class="fa fa-save me-2" *ngIf="!isLoading"></i>
            {{ isEditMode ? 'Update' : 'Create' }} Instruction
          </button>
        </div>
      </form>
    </div>
  </div>
</div>

<!-- Modal Backdrop -->
<div *ngIf="showModal" class="modal-backdrop fade show"></div>
